// Tick phase 3 (IMPLEMENTATION §2.4, §0.2 R8): chutes, downstream-first (topology.ts order), so an item can
// move up a chain as soon as the chute ahead of it emptied in the same tick.
//   A chute holds one item; an item needs `hopTicks` (5) ticks per chute. At the chain's last chute it enters
//   the sink building (acceptance re-checked without its own reservation), or drops onto the free floor
//   tile in front. A refused item waits: WRONG_ITEM when the sink cannot use it, NO_ROOM when it is full,
//   paused or still a ghost, or the chain has no sink.
//   Smart pull (R8): an empty chute whose back is a Crate pulls the highest tier its chain sink accepts now,
//   counting the items already in transit to that sink (reservations). Machines and deposits push into a
//   chute through the same check (rules/accept.ts), so a chain never carries more than its sink can take.

import { insertInto, takeFromCrate } from '../containers.ts';
import { cellIndex, stepCell } from '../core/grid.ts';
import type { Ctx } from '../ctx.ts';
import { findBuilding, restoreItem } from '../entities.ts';
import { isFree, isOpen, occAt } from '../occupancy.ts';
import { acceptReason, chainRefuses } from '../rules/accept.ts';
import { clearStall, raiseStall } from '../stall.ts';
import type { ChuteB } from '../state.ts';
import { nextChute, reserve, sinkOf, topoOf, unreserve } from '../topology.ts';

function deliver(c: Ctx, ch: ChuteB): void {
  const r = ch.carry;
  if (r === null) return;
  const next = nextChute(c, ch);
  if (next !== null) {
    if (next.carry === null) {
      next.carry = r;
      next.prog = 0;
      ch.carry = null;
      ch.prog = 0;
      c.ev.push({ e: 'chuteStart', bid: next.id, id: r.id });
      clearStall(c, ch);
    }
    return; // backed up behind the next item: the chain is moving
  }
  const topo = topoOf(c);
  const ci = cellIndex(c.occ.cols, ch.x, ch.y);
  const sink = topo.sink[ci] ?? -1;
  if (sink < 0) {
    raiseStall(c, ch, 'NO_ROOM');
    return;
  }
  const sx = sink % c.occ.cols;
  const sy = (sink - sx) / c.occ.cols;
  const id = occAt(c.occ, sx, sy);
  const b = id > 0 ? findBuilding(c.s, id) : null;
  if (b === null) {
    if (!isFree(c.occ, sx, sy)) {
      raiseStall(c, ch, 'NO_ROOM');
      return;
    }
    unreserve(c, sink, r.id);
    ch.carry = null;
    ch.prog = 0;
    restoreItem(c, { id: r.id, fam: r.fam, tier: r.tier, x: sx, y: sy });
    c.ev.push({ e: 'chuteEnd', bid: ch.id, id: r.id, x: sx, y: sy });
    clearStall(c, ch);
    return;
  }
  unreserve(c, sink, r.id);
  const why = acceptReason(
    c,
    b,
    r.fam,
    r.tier,
    topo.sinkSide[ci] ?? null,
    topo.reserved[sink] ?? [],
  );
  if (why !== null) {
    reserve(c, sink, r);
    // WRONG_ITEM only when the sink can never use it; a paused or unbuilt (ghost) sink just isn't taking
    // anything now
    const wrong = why === 'notAccepted' && b.ghost === null && !b.paused;
    raiseStall(c, ch, wrong ? 'WRONG_ITEM' : 'NO_ROOM');
    return;
  }
  ch.carry = null;
  ch.prog = 0;
  c.ev.push({ e: 'chuteEnd', bid: ch.id, id: r.id, x: sx, y: sy });
  insertInto(c, b, r, 0);
  clearStall(c, ch);
}

/** Smart pull from a Crate behind the chute: the highest tier the chain sink accepts now. */
function pull(c: Ctx, ch: ChuteB): void {
  const back = stepCell(ch.x, ch.y, ch.inDir);
  if (!isOpen(c.occ, back.x, back.y)) return;
  const id = occAt(c.occ, back.x, back.y);
  const b = id > 0 ? findBuilding(c.s, id) : null;
  if (b === null || b.type !== 'crate' || b.ghost !== null || b.paused || b.fam === null) return;
  for (let t = b.counts.length; t >= 1; t--) {
    if ((b.counts[t - 1] ?? 0) <= 0) continue;
    if (chainRefuses(c, ch, b.fam, t) !== null) continue;
    const r = takeFromCrate(c, b, t);
    ch.carry = r;
    ch.prog = 0;
    reserve(c, sinkOf(c, ch), r);
    c.ev.push({ e: 'chuteStart', bid: ch.id, id: r.id });
    return;
  }
}

export function runChutes(c: Ctx): void {
  const topo = topoOf(c);
  const hop = c.d.buildings.chute.hopTicks;
  for (const id of topo.order) {
    const ch = findBuilding(c.s, id);
    if (ch === null || ch.type !== 'chute') continue;
    if (ch.carry !== null) {
      if (ch.prog < hop) ch.prog += 1;
      if (ch.prog >= hop) deliver(c, ch);
    }
    if (ch.carry === null) pull(c, ch);
  }
}
